import api from '../../services/api'

export default {
  namespaced: true,

  state: () => ({
    user: null,
    token: localStorage.getItem('token') || null,
    loading: false,
    error: null,
    // Module catalogue (registry from server: name, description, icon, route, etc.).
    // Loaded once after login via fetchModuleCatalogue.
    moduleCatalogue: [],
    // Separate from `loading` (which only tracks login) — gateway uses this to
    // distinguish "no modules granted" from "still fetching".
    catalogueLoading: false
  }),

  mutations: {
    SET_USER(state, user) {
      state.user = user
    },
    SET_TOKEN(state, token) {
      state.token = token
      if (token) {
        localStorage.setItem('token', token)
      } else {
        localStorage.removeItem('token')
      }
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_MODULE_CATALOGUE(state, modules) {
      state.moduleCatalogue = modules
    },
    SET_CATALOGUE_LOADING(state, loading) {
      state.catalogueLoading = loading
    },
    CLEAR_AUTH(state) {
      state.user = null
      state.token = null
      state.moduleCatalogue = []
      localStorage.removeItem('token')
    }
  },

  actions: {
    /**
     * Complete login after receiving JWT from Google OAuth callback.
     * Stores token then fetches user profile from /auth/me.
     */
    async loginWithToken({ commit, dispatch }, token) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        commit('SET_TOKEN', token)
        const response = await api.get('/auth/me')
        commit('SET_USER', response.data.data.user)
        // Await catalogue so Gateway always renders the right tile state
        // on first mount. Failure is non-fatal (gateway re-fetches onMounted).
        await dispatch('fetchModuleCatalogue')
        return response.data.data.user
      } catch (error) {
        commit('CLEAR_AUTH')
        const message = error.response?.data?.error || 'Sign-in failed'
        commit('SET_ERROR', message)
        throw new Error(message)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async logout({ commit }) {
      try {
        await api.post('/auth/logout')
      } catch (error) {
        // Ignore logout errors
      }
      commit('CLEAR_AUTH')
    },

    async fetchUser({ commit, state, dispatch }) {
      if (!state.token) return null
      try {
        const response = await api.get('/auth/me')
        commit('SET_USER', response.data.data.user)
        if (state.moduleCatalogue.length === 0) {
          dispatch('fetchModuleCatalogue').catch(() => {})
        }
        return response.data.data.user
      } catch (error) {
        commit('CLEAR_AUTH')
        throw error
      }
    },

    async fetchModuleCatalogue({ commit, state }) {
      if (!state.token) return []
      commit('SET_CATALOGUE_LOADING', true)
      try {
        const res = await api.get('/auth/modules')
        commit('SET_MODULE_CATALOGUE', res.data.data.modules)
        return res.data.data.modules
      } catch (error) {
        // Non-fatal — gateway will show an empty state until retry.
        console.error('[auth] fetchModuleCatalogue failed:', error.message)
        return []
      } finally {
        commit('SET_CATALOGUE_LOADING', false)
      }
    }
  },

  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    user: (state) => state.user,
    loading: (state) => state.loading,
    error: (state) => state.error,
    // Super is a strict superset of admin — both pass admin checks.
    isAdmin: (state) => state.user?.role === 'admin' || state.user?.role === 'super',
    isSuper: (state) => state.user?.role === 'super',
    isPending: (state) => state.user?.role === 'pending',
    role: (state) => state.user?.role || null,
    // Effective module keys for the current user — populated by toJSON on the server.
    userModules: (state) => state.user?.modules || [],
    hasModule: (state) => (key) => (state.user?.modules || []).includes(key),
    moduleCatalogue: (state) => state.moduleCatalogue,
    // Module catalogue filtered to only what's granted AND not hidden from gateway.
    // Used by both the gateway tile grid AND the nav Tools dropdown — the
    // dropdown hides itself when this list has < 2 entries since switching
    // between zero or one module is meaningless.
    grantedGatewayModules: (state) => {
      const granted = new Set(state.user?.modules || [])
      return state.moduleCatalogue.filter(m => granted.has(m.key) && !m.hideFromGateway)
    },
    isGoogleConnected: (state) => !!state.user?.googleId,
    isBasecampConnected: (state) => !!state.user?.basecampAuth?.connected,
    basecampAccounts: (state) => state.user?.basecampAuth?.accounts || []
  }
}
